import "@/utils/gclid";  // capture Google Ads gclid on landing (offline conversions)

import "@/App.css";
import "@/styles/mobile-optimizations.css";
import "@/styles/mobile-dashboard-optimizations.css";
import "@/styles/visual-enhancements.css";
import "@/styles/premium-3d-design.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, lazy, Suspense } from "react";

// --- Framework / Critical Path (eager) ---
import ScrollToTop from "@/components/ScrollToTop";
import SEOHead from "@/components/SEOHead";
import PageViewTracker from "@/components/PageViewTracker";
import CampaignTracker from "@/components/CampaignTracker";
import MainLayout from "@/components/MainLayout";
import DevModeWrapper from "@/components/DevModeWrapper";
import PublicLayout from "@/components/PublicLayout";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { initializeConsent } from "@/utils/consent";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import PageLoader from "@/components/common/PageLoader";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import { ServiceUnavailableBanner } from "@/components/common/ServiceUnavailableBanner";
import { ClientProvider } from "@/contexts/ClientContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { DarkModeProvider } from "@/contexts/DarkModeContext";
import { DashboardCustomizationProvider } from "@/contexts/DashboardCustomizationContext";

// --- Landing (eager for fast first paint / LCP) ---
import LandingPagePremium from "@/pages/LandingPagePremium";
import PillarHub from "@/pages/public/PillarHub";
// Express ad-conversion landing — eager so paid-ad clicks get an instant,
// bulletproof first paint (no lazy-chunk round-trip on the money page).
import ExpressRegister from "@/pages/ExpressRegister";
import CrmPreview from "@/pages/CrmPreview";
import PublicQuote from "@/pages/PublicQuote";

// --- Auth & misc pages (lazy: not needed for the homepage's first paint, so
//     keeping them out of the main bundle shrinks parse/eval time => lower TBT) ---
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const CustomerLogin = lazy(() => import("@/pages/CustomerLogin"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const CRMLoginPage = lazy(() => import("@/pages/CRMLoginPage"));
const TechAdminLoginPage = lazy(() => import("@/pages/TechAdminLoginPage"));
const RegisterPremium = lazy(() => import("@/pages/RegisterPremium"));
const VerifiedFinancials = lazy(() => import("@/pages/VerifiedFinancials"));
const BorrowerSubmit = lazy(() => import("@/pages/BorrowerSubmit"));
const CertificateVerify = lazy(() => import("@/pages/CertificateVerify"));
const RegisterLean = lazy(() => import("@/pages/RegisterLean"));
const RegisterWaitlist = lazy(() => import("@/pages/RegisterWaitlist"));
const GetStarted = lazy(() => import("@/pages/GetStarted"));
const ThankYouPage = lazy(() => import("@/pages/ThankYouPage"));
const RegisterTest = lazy(() => import("@/pages/RegisterTest"));
// Eager (non-lazy) so the Google Ads landing page is baked into the main bundle
// and never depends on a separate chunk that can 404 on a cold/edge-cached pod.
import AdsLanding from "@/pages/AdsLanding";
const BriefLanding = lazy(() => import("@/pages/BriefLanding"));
const BriefManage = lazy(() => import("@/pages/BriefManage"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const PartnerManagement = lazy(() => import("@/pages/PartnerManagement"));
const ConsentSign = lazy(() => import("@/pages/ConsentSign"));
const IntakePortal = lazy(() => import("@/pages/IntakePortal"));

// --- Lazy-loaded: app dashboards ---
const RoleDashboard = lazy(() => import("@/components/dashboards/SmartDashboardRouter"));
const MyDashboardPage = lazy(() => import("@/pages/MyDashboardPage"));
const CapabilitiesPage = lazy(() => import("@/pages/CapabilitiesPage"));
const MyFocus = lazy(() => import("@/pages/MyFocus"));
const TeamInsights = lazy(() => import("@/pages/TeamInsights"));
const AddModules = lazy(() => import("@/pages/AddModules"));
const Roadmap = lazy(() => import("@/pages/Roadmap"));
const IntentDefaultRedirect = lazy(() => import("@/components/IntentDefaultRedirect"));
const LiveOperationsPremium = lazy(() => import("@/pages/LiveOperationsPremium"));
const ManagementDashboardAI = lazy(() => import("@/pages/ManagementDashboardAI"));
const ModulesOverview = lazy(() => import("@/pages/ModulesOverview"));
const MarketIntelligence = lazy(() => import("@/pages/MarketIntelligence"));
const CorvanaAI = lazy(() => import("@/pages/CorvanaAI"));
const MarketingIntelligence = lazy(() => import("@/pages/MarketingIntelligence"));
const EmailMarketingPlatform = lazy(() => import("@/pages/EmailMarketingPlatform"));
const SandboxIntegrationsPage = lazy(() => import("@/pages/SandboxIntegrationsPage"));
const DemoBlastsPage = lazy(() => import("@/pages/DemoBlastsPage"));
const DataPermissionsPage = lazy(() => import("@/pages/DataPermissionsPage"));
const ReviewerPortal = lazy(() => import("@/pages/ReviewerPortal"));
const ReviewCapture = lazy(() => import("@/pages/ReviewCapture"));
const FoundationReviews = lazy(() => import("@/pages/FoundationReviews"));
const RetentionDashboard = lazy(() => import("@/pages/RetentionDashboard"));
const WinBack = lazy(() => import("@/pages/WinBack"));
const DesignConcept = lazy(() => import("@/pages/DesignConcept"));
const SubscriptionSettingsPage = lazy(() => import("@/pages/SubscriptionSettingsPage"));
const Projections = lazy(() => import("@/pages/Financials"));
const CorvanaCRM = lazy(() => import("@/pages/CorvanaCRM"));
const ProspectingRedirect = lazy(() => import("@/components/ProspectingRedirect"));
const WaitlistInbox = lazy(() => import("@/pages/WaitlistInbox"));
const ValuationStudio = lazy(() => import("@/pages/ValuationStudio"));
const CampaignIntelligence = lazy(() => import("@/pages/CampaignIntelligence"));
const ProspectReport = lazy(() => import("@/pages/public/ProspectReport"));
const BookACall = lazy(() => import("@/pages/BookACall"));
const ManageBooking = lazy(() => import("@/pages/ManageBooking"));
const MeetingsAdmin = lazy(() => import("@/pages/MeetingsAdmin"));
const ScorecardPage = lazy(() => import("@/pages/public/ScorecardPage"));
const BusinessSearch = lazy(() => import("@/pages/BusinessSearch"));
const AccountBilling = lazy(() => import("@/pages/AccountBilling"));
const HealthCheck = lazy(() => import("@/pages/HealthCheck"));
const TeamManagement = lazy(() => import("@/pages/TeamManagement"));
const StaffTransitions = lazy(() => import("@/pages/StaffTransitions"));
const StaffPro = lazy(() => import("@/pages/StaffPro"));
const MyJourney = lazy(() => import("@/pages/MyJourney"));
const IndustryBenchmarking = lazy(() => import("@/pages/IndustryBenchmarking"));
const ScenarioPlanning = lazy(() => import("@/pages/ScenarioPlanning"));
const ActionPlansPage = lazy(() => import("@/pages/ActionPlansPage"));

// --- Lazy-loaded: settings & profile ---
const ProfileSettings = lazy(() => import("@/pages/ProfileSettings"));
const SecuritySettings = lazy(() => import("@/pages/SecuritySettings"));
const CompaniesGroups = lazy(() => import("@/pages/CompaniesGroups"));
const AccountantPortal = lazy(() => import("@/pages/AccountantPortal"));
const PartnerCommandCentre = lazy(() => import("@/pages/PartnerCommandCentre"));
const BulkOnboarding = lazy(() => import("@/pages/BulkOnboarding"));
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const StaffPermissions = lazy(() => import("@/pages/StaffPermissions"));
const BusinessSettings = lazy(() => import("@/pages/BusinessSettings"));
const StaffLocationManagement = lazy(() => import("@/pages/StaffLocationManagement"));
const SettingsModulesPage = lazy(() => import("@/pages/SettingsModulesPage"));
const RegulatoryCompliance = lazy(() => import("@/pages/RegulatoryCompliance"));
const LegalAssistant = lazy(() => import("@/pages/LegalAssistant"));
const LawyerSignOff = lazy(() => import("@/pages/LawyerSignOff"));
const CustomerProfile = lazy(() => import("@/pages/CustomerProfile"));
const ClientAccountPage = lazy(() => import("@/pages/ClientAccountPage"));
const ClientSetupAdmin = lazy(() => import("@/pages/ClientSetupAdmin"));
const AdminSyncDashboard = lazy(() => import("@/pages/AdminSyncDashboard"));

// --- Lazy-loaded: integrations ---
const UnifiedIntegrationsHub = lazy(() => import("@/pages/UnifiedIntegrationsHub"));
const AccountingIntegrationsPage = lazy(() => import("@/pages/AccountingIntegrationsPage"));
const ForensicAccountingPage = lazy(() => import("@/pages/ForensicAccountingPage"));
const BookkeeperPage = lazy(() => import("@/pages/BookkeeperPage"));
const IntegrationSuccess = lazy(() => import("@/pages/IntegrationSuccess"));
const IntegrationError = lazy(() => import("@/pages/IntegrationError"));
const IntegrationCallback = lazy(() => import("@/pages/IntegrationCallback"));
const IntegrationLoading = lazy(() => import("@/pages/IntegrationLoading"));
const IntegrationStatusDashboard = lazy(() => import("@/pages/IntegrationStatusDashboard"));
const IntegrationSetupWizard = lazy(() => import("@/pages/IntegrationSetupWizard"));
const ApiDocumentation = lazy(() => import("@/pages/ApiDocumentation"));
const WhiteLabelPortal = lazy(() => import("@/pages/WhiteLabelPortal"));
const MarketingCallback = lazy(() => import("@/pages/MarketingCallback"));

// --- Lazy-loaded: registration / onboarding ---
const ClientRegister = lazy(() => import("@/pages/ClientRegister"));
const ClientSetupWizard = lazy(() => import("@/pages/ClientSetupWizard"));
const SetupConnectGate = lazy(() => import("@/pages/SetupConnectGate"));
const VerifyEmailPage = lazy(() => import("@/pages/VerifyEmailPage"));

// --- Lazy-loaded: auxiliary / public info ---
const Pricing = lazy(() => import("@/pages/Pricing"));
const PricingPreview = lazy(() => import("@/pages/PricingPreview"));
const BillingFlowPreview = lazy(() => import("@/pages/BillingFlowPreview"));
const MarketplacePage = lazy(() => import("@/pages/public/MarketplacePage"));
const GrantsCompliancePage = lazy(() => import("@/pages/public/GrantsCompliancePage"));
const ReviewCollectionPage = lazy(() => import("@/pages/ReviewCollectionPage"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/public/PrivacyPolicy"));
const StatusPage = lazy(() => import("@/pages/Status"));
const DownloadCapabilityStatement = lazy(() => import("@/pages/DownloadCapabilityStatement"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const ForensicValuationModule = lazy(() => import("@/pages/ForensicValuationModule"));
const BestBISoftwareAustralia = lazy(() => import("@/pages/BestBISoftwareAustralia"));
const SelfServiceAnalyticsAustralia = lazy(() => import("@/pages/SelfServiceAnalyticsAustralia"));
const RealTimeKpiAnalyticsAustralia = lazy(() => import("@/pages/RealTimeKpiAnalyticsAustralia"));
const IndustryBiSoftwarePage = lazy(() => import("@/pages/IndustryBiSoftwarePage"));
const ForensicValuationWorkbench = lazy(() => import("@/pages/ForensicValuationWorkbench"));
const InteractiveDemo = lazy(() => import("@/pages/InteractiveDemo"));
const RequestDemo = lazy(() => import("@/pages/RequestDemo"));
const SupportTicketPage = lazy(() => import("@/pages/SupportTicketPage"));

// --- Lazy-loaded: Stripe / billing ---
const StripeCheckout = lazy(() => import("@/components/stripe/StripeCheckout"));
const SubscriptionSuccess = lazy(() => import("@/pages/SubscriptionSuccess"));
const SubscriptionCancel = lazy(() => import("@/pages/SubscriptionCancel"));

// --- Lazy-loaded: tech / staff panels ---
const TechAdmin = lazy(() => import("@/components/admin/TechAdmin"));
const StaffPerformanceManager = lazy(() => import("@/components/staff/StaffPerformanceManager"));
const StaffSelfServicePortal = lazy(() => import("@/components/staff/StaffSelfServicePortal"));

// --- Lazy-loaded: public marketing pages ---
const IndustriesPage = lazy(() => import("@/pages/public/IndustriesPage"));
const IntegrationsShowcasePage = lazy(() => import("@/pages/public/IntegrationsShowcasePage"));
const DataSourcesPage = lazy(() => import("@/pages/public/DataSourcesPage"));
const PartnerProgramPage = lazy(() => import("@/pages/public/PartnerProgramPage"));
const WhiteLabelLandingPage = lazy(() => import("@/pages/public/WhiteLabelLandingPage"));
const ApiLandingPage = lazy(() => import("@/pages/public/ApiLandingPage"));
const CoryStaffProLanding = lazy(() => import("@/pages/public/CoryStaffProLanding"));
const PrivacySettings = lazy(() => import("@/pages/public/PrivacySettings"));
const BlogPage = lazy(() => import("@/pages/public/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/public/BlogPostPage"));
const FAQPage = lazy(() => import("@/pages/public/FAQPage"));
const SecurityPage = lazy(() => import("@/pages/public/SecurityPage"));
const TechnologyPage = lazy(() => import("@/pages/public/TechnologyPage"));

// --- Lazy-loaded: SEO anchor pages ---
const RealTimeBusinessIntelligence = lazy(() => import("@/pages/seo/RealTimeBusinessIntelligence"));
const AIBusinessAnalyticsAustralia = lazy(() => import("@/pages/seo/AIBusinessAnalyticsAustralia"));
const BusinessValuationSoftware = lazy(() => import("@/pages/seo/BusinessValuationSoftware"));
const ComplianceAutomationPlatform = lazy(() => import("@/pages/seo/ComplianceAutomationPlatform"));
const MultiLocationAnalytics = lazy(() => import("@/pages/seo/MultiLocationAnalytics"));
const AustralianCRMPlatform = lazy(() => import("@/pages/seo/AustralianCRMPlatform"));
const AnswersHub = lazy(() => import("@/pages/public/AnswersHub"));
const AnswerPage = lazy(() => import("@/pages/public/AnswerPage"));
const BiComparisonHub = lazy(() => import("@/pages/public/BiComparisonHub"));
const CompareHub = lazy(() => import("@/pages/CompareHub"));
const BestAiBiTools = lazy(() => import("@/pages/BestAiBiTools"));
const CraveableTeaser = lazy(() => import("@/pages/CraveableTeaser"));
const PitchCraveable = lazy(() => import("@/pages/PitchCraveable"));
const CraveableAppDemo = lazy(() => import("@/pages/CraveableAppDemo"));
const BbfitTeaser = lazy(() => import("@/pages/BbfitTeaser"));
const BbfitBrandPage = lazy(() => import("@/pages/BbfitBrandPage"));
const PitchBbfit = lazy(() => import("@/pages/PitchBbfit"));
const PitchBroadsheet = lazy(() => import("@/pages/PitchBroadsheet"));
const PitchCorvana = lazy(() => import("@/pages/PitchCorvana"));
const IndustryPitchDeck = lazy(() => import("@/pages/IndustryPitchDeck"));
const CorvanaGrantDeck = lazy(() => import("@/pages/CorvanaGrantDeck"));
const CorvanaSecurityOnePager = lazy(() => import("@/pages/CorvanaSecurityOnePager"));
const FactsAudit = lazy(() => import("@/pages/FactsAudit"));
const ShareView = lazy(() => import("@/pages/ShareView"));
const ResolveProposal = lazy(() => import("@/pages/ResolveProposal"));
const ForensicWorkspace = lazy(() => import("@/pages/ForensicWorkspace"));
const ResolveLicensingBrief = lazy(() => import("@/pages/ResolveLicensingBrief"));
const BroadsheetTeaser = lazy(() => import("@/pages/BroadsheetTeaser"));
const DemoAttention = lazy(() => import("@/pages/DemoAttention"));

// --- Lazy-loaded: industry landing pages (20 of them — big win) ---
const HealthcareLanding = lazy(() => import("@/pages/industries/HealthcareLanding"));
const HospitalityLanding = lazy(() => import("@/pages/industries/HospitalityLanding"));
const FranchiseLanding = lazy(() => import("@/pages/industries/FranchiseLanding"));
const RetailLanding = lazy(() => import("@/pages/industries/RetailLanding"));
const ConstructionLanding = lazy(() => import("@/pages/industries/ConstructionLanding"));
const TradesLanding = lazy(() => import("@/pages/industries/TradesLanding"));
const ProfessionalLanding = lazy(() => import("@/pages/industries/ProfessionalLanding"));
const TechnologyLanding = lazy(() => import("@/pages/industries/TechnologyLanding"));
const FinanceLanding = lazy(() => import("@/pages/industries/FinanceLanding"));
const EducationLanding = lazy(() => import("@/pages/industries/EducationLanding"));
const AutomotiveLanding = lazy(() => import("@/pages/industries/AutomotiveLanding"));
const BeautyLanding = lazy(() => import("@/pages/industries/BeautyLanding"));
const FitnessLanding = lazy(() => import("@/pages/industries/FitnessLanding"));
const LegalLanding = lazy(() => import("@/pages/industries/LegalLanding"));
const AccountingLanding = lazy(() => import("@/pages/industries/AccountingLanding"));
const PropertyLanding = lazy(() => import("@/pages/industries/PropertyLanding"));
const ManufacturingLanding = lazy(() => import("@/pages/industries/ManufacturingLanding"));
const AgricultureLanding = lazy(() => import("@/pages/industries/AgricultureLanding"));
const LogisticsLanding = lazy(() => import("@/pages/industries/LogisticsLanding"));
const NonprofitLanding = lazy(() => import("@/pages/industries/NonprofitLanding"));
const EventsLanding = lazy(() => import("@/pages/industries/EventsLanding"));
const ChildcareLanding = lazy(() => import("@/pages/industries/ChildcareLanding"));
const MiningLanding = lazy(() => import("@/pages/industries/MiningLanding"));
const UtilitiesLanding = lazy(() => import("@/pages/industries/UtilitiesLanding"));
const WholesaleLanding = lazy(() => import("@/pages/industries/WholesaleLanding"));
const AdminServicesLanding = lazy(() => import("@/pages/industries/AdminServicesLanding"));
const PublicAdminLanding = lazy(() => import("@/pages/industries/PublicAdminLanding"));

// --- Lazy-loaded: tools (currently disabled in routes but imported; keep lazy anyway) ---
// ROICalculator / BusinessAssessment / ResourcesPage — routes currently commented out, skip imports entirely

function App() {
  // Initialize consent management and analytics on app load
  useEffect(() => {
    initializeConsent();
    // Capture ?ref=CODE referral attribution as early as possible.
    import('@/utils/referral').then((m) => m.captureRefFromUrl()).catch(() => {});
    // Capture Google Ads gclid + UTM attribution on landing so it can be tied
    // to the CRM enquiry when a lead form is submitted.
    import('@/utils/attribution').then((m) => m.captureAttribution()).catch(() => {});
    // Proactive support telemetry — global error/rejection listeners + the
    // authFetch hook for server failures. Fail-soft, auth-gated.
    import('@/utils/clientTelemetry').then((m) => m.initClientTelemetry()).catch(() => {});
    // Real-user Core Web Vitals — deferred off the critical path.
    const startCWV = () => import('@/utils/webVitals').then((m) => m.initWebVitals()).catch(() => {});
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    if (document.readyState === 'complete') idle(startCWV);
    else window.addEventListener('load', () => idle(startCWV), { once: true });
  }, []);

  return (
    <div className="App min-h-screen">
      <HelmetProvider>
        <DarkModeProvider>
          <AuthProvider>
            <ClientProvider>
              <ThemeProvider>
                <DashboardCustomizationProvider>
                  <ErrorBoundary fallbackMessage="The application encountered an error. Please refresh the page.">
                  <BrowserRouter>
            {/* Global DB-unavailable banner (transient Mongo failover → calm retry) */}
            <ServiceUnavailableBanner />
            {/* Scroll to top on route change */}
            <ScrollToTop />
            {/* Global SEO head — restores prerendered per-page meta after hydration */}
            <SEOHead />
            <PageViewTracker />
            <CampaignTracker />
            {/* Single DevModeWrapper provides the DEV banner for ALL routes */}
            <DevModeWrapper>
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/app" element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<IntentDefaultRedirect />} />
                  <Route path="setup/connect" element={<SetupConnectGate />} />
                  <Route path="dashboard" element={<RoleDashboard />} />
                  <Route path="my-dashboard" element={<MyDashboardPage />} />
                  <Route path="capabilities" element={<CapabilitiesPage />} />
                  <Route path="my-focus" element={<MyFocus />} />
                  <Route path="team-insights" element={<TeamInsights />} />
                  <Route path="team" element={<TeamManagement />} />
                  <Route path="staff-transitions" element={<StaffTransitions />} />
                  <Route path="staffpro" element={<StaffPro />} />
                  <Route path="my-journey" element={<MyJourney />} />
                  <Route path="add-modules" element={<AddModules />} />
                  <Route path="roadmap" element={<Roadmap />} />
                  <Route path="live-operations" element={<Navigate to="/app/management" replace />} />
                  <Route path="management" element={<ManagementDashboardAI />} />
                  <Route path="modules" element={<ModulesOverview />} />
                  <Route path="market-intelligence" element={<MarketIntelligence />} />
                  <Route path="corvana-ai" element={<CorvanaAI />} />
                  <Route path="action-plans" element={<ActionPlansPage />} />
                  <Route path="marketing-intelligence" element={<MarketingIntelligence />} />
                  <Route path="email-marketing" element={<EmailMarketingPlatform />} />
                  <Route path="data-permissions" element={<DataPermissionsPage />} />
                  <Route path="sandbox-integrations" element={<SandboxIntegrationsPage />} />
                  <Route path="demo-blasts" element={<DemoBlastsPage />} />
                  <Route path="onboarding/data-permissions" element={<DataPermissionsPage />} />
                  <Route path="financials" element={<Projections />} />
                  <Route path="projections" element={<Navigate to="/app/financials" replace />} />
                  <Route path="business-search" element={<BusinessSearch />} />
                  <Route path="verified-financials" element={<VerifiedFinancials />} />
                  <Route path="account" element={<AccountBilling />} />
                  <Route path="subscription" element={<ClientAccountPage />} />
                  <Route path="profile" element={<ProfileSettings />} />
                  <Route path="security" element={<SecuritySettings />} />
                  <Route path="companies" element={<CompaniesGroups />} />
                  <Route path="accountant-portal" element={<AccountantPortal />} />
                  <Route path="partner-command-centre" element={<PartnerCommandCentre />} />
                  <Route path="firm-command-centre" element={<PartnerCommandCentre />} />
                  <Route path="bulk-onboarding" element={<BulkOnboarding />} />
                  <Route path="user-management" element={<UserManagement />} />
                  <Route path="business-settings" element={<BusinessSettings />} />
                  <Route path="staff-locations" element={<StaffLocationManagement />} />
                  <Route path="settings/modules" element={<SettingsModulesPage />} />
                  <Route path="settings/subscription" element={<SubscriptionSettingsPage />} />
                  <Route path="staff-permissions" element={<StaffPermissions />} />
                  <Route path="staff-performance" element={<StaffPerformanceManager />} />
                  <Route path="regulatory-compliance" element={<RegulatoryCompliance />} />
                  <Route path="legal" element={<LegalAssistant />} />
                  <Route path="integrations" element={<UnifiedIntegrationsHub />} />
                  <Route path="integrations/accounting" element={<AccountingIntegrationsPage />} />
                  <Route path="forensic-accounting" element={<ForensicAccountingPage />} />
                  <Route path="partner-admin" element={<PartnerManagement />} />
                  <Route path="bookkeeper" element={<BookkeeperPage />} />
                  <Route path="sync-scheduling" element={<AdminSyncDashboard />} />
                  {/* Nested catch-all so unmatched /app/* paths show 404, not a blank shell */}
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
                <Route path="/expressregister" element={<ExpressRegister />} />
                <Route path="/crm/preview" element={<CrmPreview />} />
                <Route path="/quote/:token" element={<PublicQuote />} />
                <Route path="/login" element={<CustomerLogin />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/consent" element={<ConsentSign />} />
                <Route path="/legal/sign-off/:token" element={<LawyerSignOff />} />
                <Route path="/intake/:token" element={<IntakePortal />} />
                <Route path="/concept" element={<DesignConcept />} />
                <Route path="/register" element={<RegisterPremium />} />
                <Route path="/registerwaitlist" element={<RegisterWaitlist />} />
                <Route path="/get-started" element={<GetStarted />} />
                <Route path="/lp" element={<AdsLanding />} />
                <Route path="/thank-you/:type" element={<ThankYouPage />} />
                <Route path="/register-full" element={<RegisterPremium />} />
                <Route path="/register-test" element={<RegisterTest />} />
                <Route path="/brief" element={<BriefLanding />} />
                <Route path="/brief/manage" element={<BriefManage />} />
                <Route path="/newsletter" element={<Navigate to="/brief" replace />} />
                <Route path="/free-business-newsletter" element={<Navigate to="/brief" replace />} />
                <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                <Route path="/support/ticket" element={<SupportTicketPage />} />
                <Route path="/demo" element={<InteractiveDemo />} />
                <Route path="/request-demo" element={<RequestDemo />} />
                <Route path="/staff-portal" element={<StaffSelfServicePortal />} />
                <Route path="/health-check" element={<HealthCheck />} />
                <Route path="/corvana-crm" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <CorvanaCRM />
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/prospecting" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <ProspectingRedirect />
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/valuation" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <ValuationStudio />
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/waitlist" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <WaitlistInbox />
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/demo-attention" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <div className="mx-auto max-w-7xl p-6"><DemoAttention /></div>
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/campaign-intelligence" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <CampaignIntelligence />
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/client-setup" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <ClientSetupAdmin />
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/analytics" element={<Navigate to="/corvana-crm?tab=analytics" replace />} />
                <Route path="/corvana-crm/foundation-reviews" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <FoundationReviews />
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/retention" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <RetentionDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/corvana-crm/meetings" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <MeetingsAdmin />
                  </ProtectedRoute>
                } />
                <Route path="/book-a-call" element={<BookACall />} />
                <Route path="/booking/:token" element={<ManageBooking />} />
                <Route path="/report/:token" element={<ProspectReport />} />
                <Route path="/s/:token" element={<ShareView />} />
                <Route path="/proposals/resolve" element={<ResolveProposal />} />
                <Route path="/proposals/resolve/workspace" element={<ForensicWorkspace />} />
                <Route path="/proposals/resolve/licensing" element={<ResolveLicensingBrief />} />
                <Route path="/scorecard/:token" element={<ScorecardPage />} />
                <Route path="/analytics" element={<Navigate to="/corvana-crm?tab=analytics" replace />} />
                <Route path="/team" element={<TeamManagement />} />
                <Route path="/marketing-callback" element={<MarketingCallback />} />
                <Route path="/crm/customer/:customerId" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <CustomerProfile />
                  </ProtectedRoute>
                } />
                {/* CRM Staff Preview - Now handled via inline CRMCustomerProfileDialog */}
                <Route path="/review/:businessId" element={<ReviewCollectionPage />} />
                <Route path="/checkout" element={<StripeCheckout />} />
                <Route path="/subscription/success" element={<SubscriptionSuccess />} />
                <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
                {/* Public Pages with Header/Footer */}
                <Route path="/billing-flow-preview" element={<BillingFlowPreview />} />
                <Route path="/win-back/:token" element={<WinBack />} />
                <Route path="/pricing" element={<PublicLayout><Pricing /></PublicLayout>} />
                <Route path="/pricing-preview" element={<PublicLayout><PricingPreview /></PublicLayout>} />
                <Route path="/verified-financials" element={<VerifiedFinancials />} />
                <Route path="/borrower/:token" element={<BorrowerSubmit />} />
                <Route path="/verify/:certificateId" element={<CertificateVerify />} />
                <Route path="/modules/forensic-valuation" element={<PublicLayout><ForensicValuationModule /></PublicLayout>} />
                <Route path="/best-business-intelligence-software-australia" element={<PublicLayout><BestBISoftwareAustralia /></PublicLayout>} />
                <Route path="/self-service-analytics-software-australia" element={<PublicLayout><SelfServiceAnalyticsAustralia /></PublicLayout>} />
                <Route path="/real-time-kpi-dashboards-australia" element={<PublicLayout><RealTimeKpiAnalyticsAustralia /></PublicLayout>} />
                <Route path="/business-intelligence-software/:industry" element={<PublicLayout><IndustryBiSoftwarePage /></PublicLayout>} />
                <Route path="/forensic-valuation" element={<ForensicValuationWorkbench />} />
                <Route path="/marketplace" element={<PublicLayout><MarketplacePage /></PublicLayout>} />
                <Route path="/grants" element={<PublicLayout><GrantsCompliancePage /></PublicLayout>} />
                <Route path="/about" element={<PublicLayout><AboutUs /></PublicLayout>} />
                <Route path="/benchmarking" element={<PublicLayout><IndustryBenchmarking /></PublicLayout>} />
                <Route path="/scenarios" element={<PublicLayout><ScenarioPlanning /></PublicLayout>} />
                <Route path="/contact" element={<PublicLayout><ContactUs /></PublicLayout>} />
                <Route path="/terms" element={<PublicLayout><TermsOfService /></PublicLayout>} />
                <Route path="/privacy" element={<PublicLayout><PrivacyPolicy /></PublicLayout>} />
                <Route path="/status" element={<StatusPage />} />
                <Route path="/download-capability" element={<PublicLayout><DownloadCapabilityStatement /></PublicLayout>} />
                {/* New Public Landing Pages */}
                <Route path="/industries" element={<PublicLayout><IndustriesPage /></PublicLayout>} />
                <Route path="/integrations" element={<PublicLayout><IntegrationsShowcasePage /></PublicLayout>} />
                <Route path="/data-sources" element={<PublicLayout><DataSourcesPage /></PublicLayout>} />
                <Route path="/partner" element={<PublicLayout><PartnerProgramPage /></PublicLayout>} />
                <Route path="/white-label" element={<PublicLayout><WhiteLabelLandingPage /></PublicLayout>} />
                <Route path="/developers" element={<PublicLayout><ApiLandingPage /></PublicLayout>} />
                {/* Pillar hub pages (topic clusters) */}
                <Route path="/ai-business-intelligence" element={<PublicLayout><PillarHub /></PublicLayout>} />
                <Route path="/ai-consulting" element={<PublicLayout><PillarHub /></PublicLayout>} />
                <Route path="/ai-for-australian-business" element={<PublicLayout><PillarHub /></PublicLayout>} />
                <Route path="/ai-for-franchise-restaurants" element={<PublicLayout><PillarHub /></PublicLayout>} />
                {/* SEO/LLM Anchor Pages - Solutions */}
                <Route path="/real-time-business-intelligence" element={<PublicLayout><RealTimeBusinessIntelligence /></PublicLayout>} />
                <Route path="/ai-business-analytics-australia" element={<PublicLayout><AIBusinessAnalyticsAustralia /></PublicLayout>} />
                <Route path="/business-valuation-software" element={<PublicLayout><BusinessValuationSoftware /></PublicLayout>} />
                <Route path="/compliance-automation-platform" element={<PublicLayout><ComplianceAutomationPlatform /></PublicLayout>} />
                <Route path="/multi-location-analytics" element={<PublicLayout><MultiLocationAnalytics /></PublicLayout>} />
                <Route path="/australian-crm-platform" element={<PublicLayout><AustralianCRMPlatform /></PublicLayout>} />
                {/* AEO Answer Engine pages */}
                <Route path="/answers" element={<PublicLayout><AnswersHub /></PublicLayout>} />
                <Route path="/answers/:slug" element={<PublicLayout><AnswerPage /></PublicLayout>} />
                <Route path="/bi-software-comparison" element={<PublicLayout><BiComparisonHub /></PublicLayout>} />
                <Route path="/compare/:slug" element={<PublicLayout><CompareHub /></PublicLayout>} />
                <Route path="/best-ai-bi-tools-australia" element={<PublicLayout><BestAiBiTools /></PublicLayout>} />
                <Route path="/demo/craveable" element={<CraveableTeaser />} />
                <Route path="/demo/craveable/app" element={<CraveableAppDemo />} />
                <Route path="/pitch/craveable" element={<PitchCraveable />} />
                <Route path="/pitch/bbfit" element={<PitchBbfit />} />
                <Route path="/pitch/broadsheet" element={<PitchBroadsheet />} />
                <Route path="/pitch/corvana" element={<PitchCorvana />} />
                <Route path="/pitch/corvana/hospitality" element={<PitchCorvana />} />
                <Route path="/pitch/corvana-pitch" element={<CorvanaGrantDeck variant="corvana-pitch" />} />
                <Route path="/pitch/corvana-buy-australian" element={<CorvanaGrantDeck variant="corvana-buy-australian" />} />
                <Route path="/pitch/corvana-security" element={<CorvanaSecurityOnePager />} />
                <Route path="/pitch/facts-check" element={<ProtectedRoute requiredRoles={['super_admin']}><FactsAudit /></ProtectedRoute>} />
                <Route path="/pitch/corvana/:industry" element={<IndustryPitchDeck />} />
                <Route path="/demo/broadsheet" element={<BroadsheetTeaser />} />
                <Route path="/demo/bbfit" element={<BbfitTeaser />} />
                <Route path="/demo/bbfit/:brandSlug" element={<BbfitBrandPage />} />
                {/* Industry Landing Pages (SEO) */}
                <Route path="/industries/healthcare" element={<PublicLayout><HealthcareLanding /></PublicLayout>} />
                <Route path="/industries/hospitality" element={<PublicLayout><HospitalityLanding /></PublicLayout>} />
                <Route path="/industries/franchise" element={<PublicLayout><FranchiseLanding /></PublicLayout>} />
                <Route path="/industries/multi-location" element={<PublicLayout><FranchiseLanding /></PublicLayout>} />
                <Route path="/industries/retail" element={<PublicLayout><RetailLanding /></PublicLayout>} />
                <Route path="/industries/construction" element={<PublicLayout><ConstructionLanding /></PublicLayout>} />
                <Route path="/industries/trades" element={<PublicLayout><TradesLanding /></PublicLayout>} />
                <Route path="/industries/professional-services" element={<PublicLayout><ProfessionalLanding /></PublicLayout>} />
                <Route path="/industries/technology" element={<PublicLayout><TechnologyLanding /></PublicLayout>} />
                <Route path="/industries/finance" element={<PublicLayout><FinanceLanding /></PublicLayout>} />
                <Route path="/industries/education" element={<PublicLayout><EducationLanding /></PublicLayout>} />
                <Route path="/industries/automotive" element={<PublicLayout><AutomotiveLanding /></PublicLayout>} />
                <Route path="/industries/beauty" element={<PublicLayout><BeautyLanding /></PublicLayout>} />
                <Route path="/industries/fitness" element={<PublicLayout><FitnessLanding /></PublicLayout>} />
                <Route path="/industries/legal" element={<PublicLayout><LegalLanding /></PublicLayout>} />
                <Route path="/industries/accounting" element={<PublicLayout><AccountingLanding /></PublicLayout>} />
                <Route path="/industries/property" element={<PublicLayout><PropertyLanding /></PublicLayout>} />
                {/* New Industries */}
                <Route path="/industries/manufacturing" element={<PublicLayout><ManufacturingLanding /></PublicLayout>} />
                <Route path="/industries/agriculture" element={<PublicLayout><AgricultureLanding /></PublicLayout>} />
                <Route path="/industries/logistics" element={<PublicLayout><LogisticsLanding /></PublicLayout>} />
                <Route path="/industries/nonprofit" element={<PublicLayout><NonprofitLanding /></PublicLayout>} />
                <Route path="/industries/events" element={<PublicLayout><EventsLanding /></PublicLayout>} />
                <Route path="/industries/childcare" element={<PublicLayout><ChildcareLanding /></PublicLayout>} />
                {/* ANZSIC canonical divisions (B, D, F, N, O) */}
                <Route path="/industries/mining" element={<PublicLayout><MiningLanding /></PublicLayout>} />
                <Route path="/industries/utilities" element={<PublicLayout><UtilitiesLanding /></PublicLayout>} />
                <Route path="/industries/wholesale" element={<PublicLayout><WholesaleLanding /></PublicLayout>} />
                <Route path="/industries/admin-services" element={<PublicLayout><AdminServicesLanding /></PublicLayout>} />
                <Route path="/industries/public-sector" element={<PublicLayout><PublicAdminLanding /></PublicLayout>} />
                {/* Legacy/orphan industry slug → canonical sector page (SEO consolidation) */}
                <Route path="/industries/professional" element={<Navigate to="/industries/professional-services" replace />} />
                {/* Privacy & Legal Pages */}
                <Route path="/privacy-settings" element={<PublicLayout><PrivacySettings /></PublicLayout>} />
                {/* Content Marketing Pages - Hidden pending review */}
                <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
                <Route path="/cory" element={<PublicLayout><CoryStaffProLanding /></PublicLayout>} />
                <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />
                <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
                <Route path="/security" element={<PublicLayout><SecurityPage /></PublicLayout>} />
                <Route path="/technology" element={<PublicLayout><TechnologyPage /></PublicLayout>} />
                {/* Integration OAuth Callback Routes */}
                <Route path="/integrations/success" element={<IntegrationSuccess />} />
                <Route path="/integrations/error" element={<IntegrationError />} />
                <Route path="/integration-error" element={<IntegrationError />} />
                <Route path="/integration-callback" element={<IntegrationCallback />} />
                <Route path="/integration-loading" element={<IntegrationLoading />} />
                <Route path="/integrations/callback" element={<IntegrationCallback />} />
                <Route path="/integrations/accounting" element={<AccountingIntegrationsPage />} />
                {/* API & Admin Routes */}
                <Route path="/developer-docs" element={<ApiDocumentation />} />
                <Route path="/integration-setup" element={<IntegrationSetupWizard />} />
                <Route path="/white-label-portal" element={
                  <ProtectedRoute requiredRoles={['owner']}>
                    <WhiteLabelPortal />
                  </ProtectedRoute>
                } />
                {/* Client Onboarding Routes */}
                <Route path="/client-register" element={<ClientRegister />} />
                <Route path="/client-setup" element={<ClientSetupWizard />} />

                {/* Email Verification */}
                <Route path="/verify-email" element={<VerifyEmailPage />} />

                {/* CRM Login - Dedicated login page for Corvana CRM */}
                <Route path="/crm-login" element={<CRMLoginPage />} />
                <Route path="/corvana-crm/login" element={<CRMLoginPage />} />

                {/* Tech Admin Login & Dashboard */}
                <Route path="/tech-admin" element={<TechAdmin />} />
                <Route path="/tech-admin/login" element={<TechAdminLoginPage />} />
                <Route path="/tech-admin/dashboard" element={<TechAdmin />} />
                <Route path="/techadmin" element={<TechAdmin />} />

                {/* Reviewer Portal — magic-link, public route (token-scoped) */}
                <Route path="/reviewer" element={<ReviewerPortal />} />
                <Route path="/foundation-review/:token" element={<ReviewCapture />} />

                {/* Integration Status Dashboard */}
                <Route path="/integration-hub" element={<IntegrationStatusDashboard />} />
                <Route path="/my-integrations" element={<IntegrationStatusDashboard />} />

                <Route path="/" element={<PublicLayout><LandingPagePremium /></PublicLayout>} />

                {/* Catch-all 404 — keeps stale/dead links from rendering a blank page */}
                <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
              </Routes>
              </Suspense>
            </DevModeWrapper>
            {/* Cookie Consent Banner - appears at bottom */}
            <CookieConsentBanner />
            </BrowserRouter>
            </ErrorBoundary>
            <Toaster position="top-right" richColors offset="100px" />
          </DashboardCustomizationProvider>
          </ThemeProvider>
        </ClientProvider>
      </AuthProvider>
      </DarkModeProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
